import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import {
  listCompetitionSeasons,
  previewArchetype,
  searchEntities,
  type ArchetypeDefinition,
  type ArchetypePreview,
  type CompetitionSeasons,
  type PreviewArchetypeParams,
} from "@/lib/admin-api";
import {
  DEFAULT_COMPETITION_NAME,
  PREVIEW_DEBOUNCE_MS,
  previewRequests,
} from "@/pages/archetypes/archetype-preview-model";

export interface PreviewCompetition {
  id: string;
  name: string;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

/**
 * The Eredivisie, found by name through identity search: no route lists
 * competitions, and an id written here would be a guess. null when the
 * search has no exact match; the admin then picks one.
 */
export function useDefaultPreviewCompetition() {
  return useQuery({
    queryKey: ["archetypes", "preview", "default-competition"],
    queryFn: async (): Promise<PreviewCompetition | null> => {
      const { results } = await searchEntities({
        query: DEFAULT_COMPETITION_NAME,
        type: "competition",
        limit: 10,
      });
      const match = (results ?? []).find(
        (e) => e.name.trim().toLowerCase() === DEFAULT_COMPETITION_NAME.toLowerCase(),
      );
      return match ? { id: match.entityId, name: match.name } : null;
    },
    staleTime: Infinity,
    retry: false,
  });
}

/** One competition's seasons, most recent first (admin-service PF-A2b). */
export function useCompetitionSeasons(competitionId: string | undefined) {
  return useQuery<CompetitionSeasons>({
    queryKey: ["archetypes", "preview", "seasons", competitionId ?? ""],
    queryFn: () => listCompetitionSeasons(competitionId ?? ""),
    enabled: Boolean(competitionId),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

export interface PreviewGroupState {
  position: string;
  preview?: ArchetypePreview;
  loading: boolean;
  /** Set while a newer request than the shown preview is in flight. */
  refreshing: boolean;
  error: Error | null;
}

export interface ArchetypePreviewState {
  competition: PreviewCompetition | null;
  competitionLoading: boolean;
  setCompetition: (competition: PreviewCompetition) => void;
  seasons: CompetitionSeasons | undefined;
  seasonsLoading: boolean;
  seasonsError: Error | null;
  seasonId: string;
  setSeasonId: (seasonId: string) => void;
  /** One per home position of the definition, in display order. */
  groups: PreviewGroupState[];
  /** False when the definition cannot be scored (no preview runs). */
  enabled: boolean;
  /** True when the groups score the definition as it stands, not an earlier one. */
  current: boolean;
  /** True when the scored definition needs event data. */
  eventOnly: boolean;
}

/**
 * The live preview of an unsaved definition (profile-spec.md §5): one
 * PreviewArchetype per home position, 400 ms after the last edit, over the
 * chosen competition season. The last answer stays on screen while the next
 * one loads, so typing a weight does not blank the lists.
 */
export function useArchetypePreview({
  archetypeId,
  definition,
  enabled,
}: {
  archetypeId: string;
  definition: ArchetypeDefinition;
  enabled: boolean;
}): ArchetypePreviewState {
  const defaultCompetition = useDefaultPreviewCompetition();
  const [picked, setPicked] = useState<PreviewCompetition | null>(null);
  const competition = picked ?? defaultCompetition.data ?? null;

  const seasonsQuery = useCompetitionSeasons(competition?.id);
  // The season choice belongs to one competition: picking another
  // competition goes back to its current season.
  const [pickedSeason, setPickedSeason] = useState<{ competitionId: string; seasonId: string }>();
  const seasonIds = seasonsQuery.data?.seasons.map((s) => s.id) ?? [];
  const seasonId =
    pickedSeason &&
    pickedSeason.competitionId === competition?.id &&
    seasonIds.includes(pickedSeason.seasonId)
      ? pickedSeason.seasonId
      : (seasonsQuery.data?.currentSeasonId ?? "");

  const debouncedDefinition = useDebouncedValue(definition, PREVIEW_DEBOUNCE_MS);
  const debouncedEnabled = useDebouncedValue(enabled, PREVIEW_DEBOUNCE_MS);
  const live = enabled && debouncedEnabled;

  const requests = useMemo(
    () =>
      competition && seasonId
        ? previewRequests(archetypeId, debouncedDefinition, competition.id, seasonId)
        : [],
    [archetypeId, debouncedDefinition, competition, seasonId],
  );

  const results = useQueries({
    queries: requests.map((params) => ({
      queryKey: ["archetypes", "preview", "run", params] as const,
      queryFn: () => previewArchetype(params),
      enabled: live,
      staleTime: 60 * 1000,
      retry: false,
      // The last answer for the same home position stays up while the next
      // loads. Not another position's: adding a position shifts the list.
      placeholderData: (
        previous: ArchetypePreview | undefined,
        previousQuery?: { queryKey: readonly unknown[] },
      ) => {
        const previousParams = previousQuery?.queryKey[3] as PreviewArchetypeParams | undefined;
        return previousParams?.homePositions[0] === params.homePositions[0] ? previous : undefined;
      },
    })),
  });

  const groups: PreviewGroupState[] = requests.map((params, index) => {
    const result = results[index];
    return {
      position: params.homePositions[0],
      preview: live ? result?.data : undefined,
      loading: live && Boolean(result?.isPending),
      refreshing: live && Boolean(result?.isPlaceholderData || result?.isFetching),
      error: live ? (result?.error ?? null) : null,
    };
  });

  return {
    competition,
    competitionLoading: !picked && defaultCompetition.isPending,
    setCompetition: setPicked,
    seasons: seasonsQuery.data,
    seasonsLoading: Boolean(competition) && seasonsQuery.isPending,
    seasonsError: seasonsQuery.error ?? null,
    seasonId,
    setSeasonId: (next) => {
      if (competition) setPickedSeason({ competitionId: competition.id, seasonId: next });
    },
    groups,
    enabled: live,
    current: live && JSON.stringify(debouncedDefinition) === JSON.stringify(definition),
    eventOnly: debouncedDefinition.requiredData === "event",
  };
}
